import React from 'react';
import { Sparkles } from 'lucide-react';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { useQuotaBalance } from '../../hooks/useQuotaBalance';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { Button } from '../../design-system';

/**
 * Trial / free-plan banner shown at the top of the repository overview.
 * Hidden for paid orgs and demo orgs. "Upgrade plan" opens the Plan section
 * of the account settings modal (same entry point the header uses).
 */
const TrialBanner: React.FC = () => {
  const { accessType } = useSubscriptionAccess();
  const { currentOrganization } = useOrganization();
  const { quotaBalance } = useQuotaBalance();
  const { openAccountSettings } = useSettingsModal();

  if (currentOrganization?.is_demo) return null;
  if (accessType !== 'trial' && accessType !== 'free') return null;

  const title = accessType === 'trial' ? "You're on the free trial" : "You're on the free plan";
  const usage =
    quotaBalance != null
      ? `${quotaBalance.remaining_deep_scans} Deep AI scans and ${quotaBalance.remaining_prs} AI fixes remaining.`
      : 'Includes 1 Deep AI scan and 1 AI fix.';

  return (
    <div
      style={{
        background: 'linear-gradient(100deg, var(--lime-soft), var(--purple-soft))',
        border: '1px solid var(--lime-soft-border)',
        borderRadius: 'var(--radius-xl)',
        padding: '15px 20px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 15,
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 'var(--radius-md)',
          background: 'var(--surface-2)',
          border: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
          color: 'var(--accent-ink)',
        }}
      >
        <Sparkles style={{ width: 16, height: 16 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2, color: 'var(--text-primary)' }}>{title}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{usage}</div>
      </div>
      <Button size="sm" onClick={() => openAccountSettings('plan')}>
        Upgrade plan
      </Button>
    </div>
  );
};

export default TrialBanner;
